import { useState, useEffect, useCallback, useMemo } from "react";
import { asText } from "../lib/utils";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import PermButton from "@/components/PermButton";
import { toast } from "sonner";
import { Loader2, FileText, Plus, Paperclip, Trash2, Eye, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import useAuthStore from "@/store/authStore";
import BillHeaderFields, { PAYMENT_TERMS } from "@/components/bill/BillHeaderFields";
import BillLineItemsTable, { makeEmptyLine } from "@/components/bill/BillLineItemsTable";
import BillTotalsPanel from "@/components/bill/BillTotalsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_INVOICE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
const MAX_INVOICE_BYTES = 5 * 1024 * 1024;

// Job Work Invoice — RM-Inward-parity multi-line invoice (Feb 2026).
// Reuses `BillHeaderFields` / `BillLineItemsTable` / `BillTotalsPanel`
// so the field list, tax/discount math, and add-line UX stay 1:1 with
// RM Inward. The ONLY behavioural difference is the item picker: it's
// scoped to the vendor's uninvoiced `jobwork_receipts` rather than the
// full RM catalog (AC2/AC3).
export default function JobworkInvoices() {
  const { token, hasPermission } = useAuthStore();
  const h = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const canCreate = hasPermission("JobworkInvoice", "CREATE");

  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [recon, setRecon] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("ALL");

  // Zoho + local reference data (mirrors RM Inward).
  const [zohoAccounts, setZohoAccounts] = useState([]);
  const [zohoConfigured, setZohoConfigured] = useState(false);
  const [zohoTaxes, setZohoTaxes] = useState([]);
  const [tdsTaxes, setTdsTaxes] = useState([]);
  const [defaultJobworkAccountId, setDefaultJobworkAccountId] = useState("");

  // ── Create-dialog state ────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [editingCode, setEditingCode] = useState(null); // PLAYFIELD_DRAFT code being edited
  const [pushError, setPushError] = useState(null); // AC3 inline red banner
  const [shortfall, setShortfall] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState([]);

  // Feb 2026 — per-invoice retry state for the "Retry Zoho Push" action.
  // Keyed by invoice_code so multiple pending invoices can be retried
  // independently without their loading spinners bleeding into each other.
  const [retryingCode, setRetryingCode] = useState(null);

  // Feb 2026 — invoice being viewed in the read-only "View" dialog.
  const [viewingInvoice, setViewingInvoice] = useState(null);
  // Feb 2026 — per-attachment Zoho sync (mirrors RM Inward's dialog).
  const [zohoAttSyncing, setZohoAttSyncing] = useState(false);
  const handleSyncJwAttachmentsToZoho = async () => {
    if (!viewingInvoice?.invoice_code) return;
    setZohoAttSyncing(true);
    try {
      const res = await axios.post(
        `${API}/jobwork/invoice/${encodeURIComponent(viewingInvoice.invoice_code)}/attachment/sync-to-zoho`,
        null, { headers: h() },
      );
      const { synced = 0, failed = 0, skipped = 0, results = [] } = res.data || {};
      const firstErr = (results.find((r) => !r.synced && r.error) || {}).error;
      if (failed === 0 && synced > 0) {
        toast.success(`${synced} attachment${synced === 1 ? "" : "s"} synced to Zoho`);
      } else if (synced > 0 && failed > 0) {
        toast.warning(`${synced} synced, ${failed} failed — ${firstErr || "see details"}`);
      } else if (failed > 0) {
        toast.error(`${failed} attachment${failed === 1 ? "" : "s"} failed — ${firstErr || "see details"}`);
      } else {
        toast.info(`Nothing to sync (${skipped} skipped)`);
      }
      // JW has no per-invoice GET — merge the results locally so badges refresh
      // without a round-trip. Each `result` carries the `attachment_id` +
      // `synced` + optional `zoho_document_id` / `error`.
      setViewingInvoice((prev) => {
        if (!prev) return prev;
        const byId = new Map(results.map((r) => [r.attachment_id, r]));
        const nextAttachments = (prev.attachments || []).map((a) => {
          const r = byId.get(a.id);
          if (!r) return a;
          return r.synced
            ? { ...a, zoho_synced: true, zoho_document_id: r.zoho_document_id, zoho_last_error: null }
            : { ...a, zoho_synced: false, zoho_last_error: r.error || a.zoho_last_error };
        });
        return { ...prev, attachments: nextAttachments };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sync attachments to Zoho");
    } finally {
      setZohoAttSyncing(false);
    }
  };

  // Feb 2026 v2 — invoice-level "Sync with Zoho" (finance-accessible;
  // matches the attachment sync button's pattern). Dispatches on the
  // backend to `/retry-zoho-push` OR the status-sync approve/reject
  // path depending on `zoho_push_status`. Single button, one intent.
  const [zohoInvSyncing, setZohoInvSyncing] = useState(false);
  const handleSyncJwInvoiceWithZoho = async () => {
    if (!viewingInvoice?.invoice_code || zohoInvSyncing) return;
    setZohoInvSyncing(true);
    try {
      const res = await axios.post(
        `${API}/jobwork/invoice/${encodeURIComponent(viewingInvoice.invoice_code)}/sync-zoho`,
        null, { headers: h() },
      );
      const { branch, result, action, zoho_status, invoice: refreshed } = res.data || {};
      if (branch === "push") {
        const status = (result || {}).status;
        if (status === "PUSHED") {
          toast.success(`Zoho push succeeded (bill ${(result || {}).zoho_bill_id || "?"}).`);
        } else if (status === "REJECTED_PLAYFIELD_DRAFT") {
          toast.error(`Zoho rejected the invoice: ${(result || {}).error || "validation failed"}. Moved to PLAYFIELD_DRAFT.`);
        } else {
          toast.error(`Push not complete: ${(result || {}).error || "unknown"}. You can try again.`);
        }
      } else if (branch === "status_sync") {
        if (action === "approved") {
          toast.success(`Zoho status = ${zoho_status}. Invoice moved to APPROVED.`);
        } else if (action === "rejected") {
          toast.warning(`Zoho status = ${zoho_status}. Invoice moved to REJECTED.`);
        } else {
          toast.info(`Zoho status = ${zoho_status}. No state change.`);
        }
      }
      if (refreshed) setViewingInvoice(refreshed);
      await fetchInvoices();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || e.message || "Sync failed";
      toast.error(`Sync failed: ${msg}`);
    } finally {
      setZohoInvSyncing(false);
    }
  };

  const blankHeader = () => ({
    vendor_id: "",
    bill_number: "",
    bill_date: new Date().toISOString().split("T")[0],
    due_date: "",
    payment_terms: "NET_30",
    accounts_payable: "Trade Payables",
    // `order_number` deliberately absent — AC7 (job-work bills carry no PO).
    notes: "",
    reverse_charge: false,
  });

  const blankTotals = () => ({
    sub_total: 0,
    discount_type: "percentage",
    discount_value: 0,
    discount_amount: 0,
    tds_tcs: "NONE",
    tds_tcs_amount: 0,
    tax_total: 0,
    grand_total: 0,
  });

  const [header, setHeader] = useState(blankHeader());
  const [lineItems, setLineItems] = useState([makeEmptyLine()]);
  const [totals, setTotals] = useState(blankTotals());

  // ── Data loaders ───────────────────────────────────────────────
  const bootstrap = useCallback(async () => {
    try {
      const v = await axios.get(`${API}/vendors?is_job_worker=true`, { headers: h() });
      setVendors(Array.isArray(v.data) ? v.data : []);
    } catch (_e) { /* graceful empty */ }
    try {
      const r = await axios.get(`${API}/zoho/accounts`, { headers: h() });
      const accs = r.data?.chart_of_accounts || r.data?.accounts || [];
      setZohoAccounts(accs);
      setZohoConfigured(true);
      // Prefer an account whose name contains "job costing" — matches the
      // backend resolver so the UI default and Zoho push agree.
      const jc = accs.find((a) => (a.account_name || "").toLowerCase().includes("job costing"))
        || accs.find((a) => (a.account_name || "").toLowerCase().includes("job work"));
      if (jc) setDefaultJobworkAccountId(jc.account_id);
    } catch (_e) { setZohoConfigured(false); }
    try {
      const r = await axios.get(`${API}/zoho/taxes`, { headers: h() });
      setZohoTaxes(r.data?.taxes || []);
    } catch (_e) { /* optional */ }
    try {
      const r = await axios.get(`${API}/tds-taxes?status=ACTIVE`, { headers: h() });
      setTdsTaxes(r.data || []);
    } catch (_e) { /* optional */ }
  }, [h]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (vendorFilter !== "ALL") p.set("vendor_id", vendorFilter);
      p.set("limit", "200");
      const res = await axios.get(`${API}/jobwork/invoices?${p.toString()}`, { headers: h() });
      setInvoices(res.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load invoices"); }
    finally { setLoading(false); }
  }, [vendorFilter, h]);

  const fetchRecon = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (vendorFilter !== "ALL") p.set("vendor_id", vendorFilter);
      const res = await axios.get(`${API}/jobwork/reconciliation?${p.toString()}`, { headers: h() });
      setRecon(res.data || { items: [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load reconciliation"); }
  }, [vendorFilter, h]);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { fetchInvoices(); fetchRecon(); }, [fetchInvoices, fetchRecon]);

  // Feb 2026 — Retry Zoho Push for an invoice stuck at PUSH_PENDING.
  // Calls the existing `/jobwork/invoice/{code}/retry-zoho-push`
  // endpoint (same permission as /submit). On success the list row's
  // `zoho_push_status` badge flips to PUSHED via `fetchInvoices()`;
  // on failure the invoice stays at PUSH_PENDING with the new error
  // and the button remains available for another attempt.
  const handleRetryZohoPush = useCallback(async (invoiceCode) => {
    if (retryingCode) return;
    setRetryingCode(invoiceCode);
    try {
      const r = await axios.post(
        `${API}/jobwork/invoice/${encodeURIComponent(invoiceCode)}/retry-zoho-push`,
        null, { headers: h() },
      );
      const result = r.data?.result || {};
      if (result.status === "PUSHED") {
        toast.success(`Zoho push succeeded (bill ${result.zoho_bill_id || "?"}).`);
      } else if (result.status === "REJECTED_PLAYFIELD_DRAFT") {
        toast.error(`Zoho rejected the invoice: ${result.error || "validation failed"}. Moved to PLAYFIELD_DRAFT.`);
      } else {
        // AC2 failure branch — stays PUSH_PENDING, error surfaced.
        toast.error(`Retry failed: ${result.error || "Zoho push did not complete"}. You can try again.`);
      }
      await fetchInvoices();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || e.message || "Retry failed";
      toast.error(`Retry failed: ${msg}`);
    } finally {
      setRetryingCode(null);
    }
  }, [h, retryingCode, fetchInvoices]);

  // AC2/AC3 — item picker scoped to vendor's uninvoiced receipts.
  const fetchAvailableItems = useCallback(async (vendorId) => {
    if (!vendorId) { setAvailableItems([]); return; }
    setLoadingItems(true);
    try {
      const res = await axios.get(
        `${API}/jobwork/invoice/available-items?vendor_id=${vendorId}`,
        { headers: h() },
      );
      setAvailableItems(res.data?.items || []);
    } catch (e) {
      setAvailableItems([]);
      toast.error(e?.response?.data?.detail || "Failed to load available items");
    } finally { setLoadingItems(false); }
  }, [h]);

  // ── Due-date auto-compute from payment terms ───────────────────
  useEffect(() => {
    if (header.payment_terms === "CUSTOM") return;
    const pt = PAYMENT_TERMS.find((t) => t.value === header.payment_terms);
    if (pt && pt.days !== null && header.bill_date) {
      const d = new Date(header.bill_date);
      d.setDate(d.getDate() + pt.days);
      setHeader((prev) => ({ ...prev, due_date: d.toISOString().split("T")[0] }));
    }
  }, [header.payment_terms, header.bill_date]);

  // ── Reset ──────────────────────────────────────────────────────
  const resetForm = () => {
    setHeader(blankHeader());
    setLineItems([makeEmptyLine()]);
    setTotals(blankTotals());
    setAvailableItems([]);
    setInvoiceFiles([]);
    setShortfall(null);
    setPushError(null);
    setEditingCode(null);
  };

  // Load a PLAYFIELD_DRAFT invoice into the form for editing.
  const openDraftForEdit = async (inv) => {
    setEditingCode(inv.invoice_code);
    setHeader({
      vendor_id: inv.vendor_id,
      bill_number: inv.bill_number || "",
      bill_date: inv.bill_date || new Date().toISOString().split("T")[0],
      due_date: inv.due_date || "",
      payment_terms: inv.payment_terms || "NET_30",
      accounts_payable: inv.accounts_payable || "Trade Payables",
      reverse_charge: !!inv.reverse_charge,
      notes: inv.notes || "",
    });
    setLineItems((inv.line_items || []).map((li) => ({
      rm_id: li.rm_id, rm_search: li.rm_id,
      description: asText(li.description), hsn: asText(li.hsn_or_sac),
      unit: li.unit || "", quantity: li.quantity, rate: li.rate,
      tax: li.tax_name || "GST_18", tax_id: li.tax_id || "",
      amount: li.amount || 0, account_id: li.account_id || "",
      available_qty: undefined,
    })));
    setTotals({
      sub_total: inv.sub_total || 0,
      discount_type: inv.discount_type || "percentage",
      discount_value: inv.discount_value || 0,
      discount_amount: inv.discount_amount || 0,
      tds_tcs: inv.tds_tax_id || "NONE",
      tds_tcs_amount: 0,
      tax_total: inv.tax_total || 0,
      grand_total: inv.grand_total || 0,
    });
    setPushError(inv.zoho_push_error ? { message: inv.zoho_push_error, code: inv.zoho_push_error_code } : null);
    setShowCreate(true);
    // Kick off item-search so the picker is populated when the user edits lines.
    await fetchAvailableItems(inv.vendor_id);
  };

  const onVendorPick = (vendorId) => {
    setHeader((prev) => ({ ...prev, vendor_id: vendorId }));
    setLineItems([makeEmptyLine()]);
    fetchAvailableItems(vendorId);
  };

  // Build the same payload shape both `/draft` and `/invoice` (or /submit) use.
  const buildPayload = (validLines) => ({
    vendor_id: header.vendor_id,
    bill_number: header.bill_number,
    bill_date: header.bill_date,
    due_date: header.due_date || null,
    payment_terms: header.payment_terms || "NET_30",
    accounts_payable: header.accounts_payable || "Trade Payables",
    reverse_charge: !!header.reverse_charge,
    notes: header.notes || null,
    discount_type: totals.discount_type || null,
    discount_value: Number(totals.discount_value || 0),
    tds_tax_id: totals.tds_tcs && totals.tds_tcs !== "NONE" ? totals.tds_tcs : null,
    line_items: validLines.map((li) => ({
      rm_id: li.rm_id,
      description: asText(li.description) || null,
      unit: li.unit || null,
      hsn_or_sac: asText(li.hsn) || null,
      quantity: Number(li.quantity),
      rate: Number(li.rate),
      tax_id: li.tax_id || null,
      tax_name: li.tax || null,
      tax_percent: li.tax_id
        ? (parseFloat(zohoTaxes.find((t) => t.tax_id === li.tax_id)?.tax_percentage) || 0)
        : (({ NONE: 0, GST_5: 5, GST_12: 12, GST_18: 18, GST_28: 28 })[li.tax] || 0),
      account_id: li.account_id || defaultJobworkAccountId || null,
    })),
  });

  const validateForm = () => {
    if (!header.vendor_id) { toast.error("Pick a vendor first"); return null; }
    if (!header.bill_number) { toast.error("Bill # is required"); return null; }
    if (!header.bill_date) { toast.error("Bill Date is required"); return null; }
    const validLines = lineItems.filter((li) => li.rm_id && Number(li.quantity) > 0 && Number(li.rate) > 0);
    if (validLines.length === 0) { toast.error("Add at least one line item with qty & rate"); return null; }
    return validLines;
  };

  const uploadAttachments = async (invoiceCode) => {
    if (!invoiceCode || invoiceFiles.length === 0) return;
    for (const file of invoiceFiles) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(
          `${API}/jobwork/invoice/${invoiceCode}/attachment`,
          fd,
          { headers: { ...h(), "Content-Type": "multipart/form-data" } },
        );
      } catch (e) {
        toast.error(`Attachment ${file.name} failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
  };

  // AC1 — "Save Draft" persists as PLAYFIELD_DRAFT (no Zoho, no setoff).
  const saveDraft = async () => {
    setShortfall(null); setPushError(null);
    const validLines = validateForm();
    if (!validLines) return;
    setSavingDraft(true);
    try {
      const payload = buildPayload(validLines);
      let res, invoiceCode;
      if (editingCode) {
        res = await axios.put(`${API}/jobwork/invoice/${editingCode}/draft`, payload, { headers: h() });
        invoiceCode = editingCode;
      } else {
        res = await axios.post(`${API}/jobwork/invoice/draft`, payload, { headers: h() });
        invoiceCode = res.data?.invoice_code;
      }
      await uploadAttachments(invoiceCode);
      toast.success(`${invoiceCode} saved as Playfield Draft`);
      setShowCreate(false);
      resetForm();
      fetchInvoices();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to save draft"));
    } finally { setSavingDraft(false); }
  };

  const submitCreate = async () => {
    setShortfall(null); setPushError(null);
    const validLines = validateForm();
    if (!validLines) return;

    setCreating(true);
    try {
      const payload = buildPayload(validLines);

      // AC2 — an existing PLAYFIELD_DRAFT being edited goes through
      // `PUT /draft` first (to persist any edits) then `POST /submit`.
      // A fresh invoice hits the atomic create-and-push path.
      let invoiceCode, invoice;
      if (editingCode) {
        await axios.put(`${API}/jobwork/invoice/${editingCode}/draft`, payload, { headers: h() });
        const sres = await axios.post(`${API}/jobwork/invoice/${editingCode}/submit`, {}, { headers: h() });
        invoiceCode = editingCode;
        invoice = sres.data || {};
        // AC3 — server flipped the row to PLAYFIELD_DRAFT on permanent 4xx.
        if (invoice?.saved_as_draft) {
          setPushError({ message: invoice.error, code: invoice.error_code });
          toast.error("Zoho rejected the invoice — saved as Playfield Draft. Fix and retry.");
          fetchInvoices();
          return;
        }
      } else {
        const res = await axios.post(`${API}/jobwork/invoice`, payload, { headers: h() });
        invoice = res.data?.invoice || {};
        invoiceCode = res.data?.invoice_code;
        // AC3 — atomic create-and-push also flips to PLAYFIELD_DRAFT.
        if (invoice?.status === "PLAYFIELD_DRAFT" && invoice?.zoho_push_error) {
          setPushError({
            message: invoice.zoho_push_error,
            code: invoice.zoho_push_error_code,
          });
          setEditingCode(invoiceCode);
          toast.error("Zoho rejected the invoice — saved as Playfield Draft. Fix and retry.");
          fetchInvoices();
          return;
        }
      }

      await uploadAttachments(invoiceCode);
      const pushMsg = invoice?.status === "DRAFT" ? " (Zoho: pushed, awaiting approval)"
        : invoice?.zoho_push_status ? ` (Zoho: ${invoice.zoho_push_status})` : "";
      toast.success(`${invoiceCode} submitted${pushMsg}`);
      setShowCreate(false);
      resetForm();
      fetchInvoices();
      fetchRecon();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 422) {
        const msg = typeof detail === "string" ? detail : JSON.stringify(detail, null, 2);
        setShortfall(msg);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to submit"));
      }
    } finally { setCreating(false); }
  };

  const vendorName = useMemo(
    () => Object.fromEntries((vendors || []).map((v) => [v.id, v.name])),
    [vendors],
  );

  const attachInvoiceFiles = (picked) => {
    const files = Array.from(picked || []);
    const accepted = [];
    for (const f of files) {
      if (!ALLOWED_INVOICE_TYPES.includes(f.type)) {
        toast.error(`"${f.name}" — unsupported. Use JPEG/PNG/WebP/HEIC/PDF.`);
        continue;
      }
      if (f.size > MAX_INVOICE_BYTES) {
        toast.error(`"${f.name}" too large (${(f.size / 1024 / 1024).toFixed(2)} MB). Max 5 MB.`);
        continue;
      }
      const dupe = invoiceFiles.some((x) => x.name === f.name && x.size === f.size);
      if (!dupe) accepted.push(f);
    }
    if (accepted.length > 0) setInvoiceFiles((prev) => [...prev, ...accepted]);
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="jobwork-invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-500" />
            Jobwork Invoices
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Book vendor conversion charges against uninvoiced job-work receipts.
            Multi-line invoices with per-item cap enforced against
            <span className="font-mono"> Σ received − Σ invoiced </span>
            per (vendor, item).
          </p>
        </div>
        {canCreate && (
          <PermButton entity="JobworkInvoice" action="CREATE" onClick={() => setShowCreate(true)} data-testid="inv-create-btn">
            <Plus className="w-4 h-4 mr-2" />New Invoice
          </PermButton>
        )}
      </div>

      <div className="flex items-end gap-3">
        <div className="w-64">
          <Label className="text-xs">Vendor</Label>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger data-testid="inv-vendor-flt"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="recon" data-testid="tab-recon">Reconciliation (Received vs Invoiced)</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Invoices ({invoices.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
              ) : invoices.length === 0 ? (
                <div className="p-8 text-center text-zinc-500" data-testid="inv-empty">No invoices yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Code</th>
                      <th className="px-4 py-3 text-left">Bill #</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Vendor</th>
                      <th className="px-4 py-3 text-right">Lines</th>
                      <th className="px-4 py-3 text-right">Sub Total</th>
                      <th className="px-4 py-3 text-right">Grand Total</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Zoho</th>
                      <th className="px-4 py-3 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const nLines = (inv.line_items || []).length || 1;
                      const sub = Number(inv.sub_total || inv.amount || 0);
                      const grand = Number(inv.grand_total || inv.amount || 0);
                      const status = (inv.status || "DRAFT").toUpperCase();
                      const STATUS_CLS = {
                        PLAYFIELD_DRAFT: "bg-amber-100 text-amber-900 border-amber-300",
                        DRAFT: "bg-sky-100 text-sky-900 border-sky-300",
                        APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-300",
                        REJECTED: "bg-red-100 text-red-900 border-red-300",
                        BOOKED: "bg-emerald-100 text-emerald-900 border-emerald-300", // legacy alias
                      };
                      const STATUS_LABEL = {
                        PLAYFIELD_DRAFT: "Draft",
                        DRAFT: "Awaiting Zoho",
                        APPROVED: "Approved",
                        REJECTED: "Rejected",
                        BOOKED: "Approved",
                      };
                      const isDraft = status === "PLAYFIELD_DRAFT";
                      return (
                        <tr key={inv.invoice_code} className="border-t hover:bg-zinc-50" data-testid={`inv-row-${inv.invoice_code}`}>
                          <td className="px-4 py-3 font-mono text-xs">{inv.invoice_code}</td>
                          <td className="px-4 py-3 font-mono text-xs">{inv.bill_number || inv.invoice_code}</td>
                          <td className="px-4 py-3 text-xs">{inv.bill_date || inv.invoice_date || (inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—")}</td>
                          <td className="px-4 py-3">{vendorName[inv.vendor_id] || inv.vendor_id}</td>
                          <td className="px-4 py-3 font-mono text-right text-xs">{nLines}</td>
                          <td className="px-4 py-3 font-mono text-right">{sub.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 font-mono text-right">{grand.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-xs">
                            <span
                              className={`px-2 py-0.5 rounded border text-[11px] font-semibold ${STATUS_CLS[status] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}
                              data-testid={`inv-status-${inv.invoice_code}`}
                            >
                              {STATUS_LABEL[status] || status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <span className={`px-2 py-0.5 rounded ${
                              inv.zoho_push_status === "PUSHED" ? "bg-emerald-100 text-emerald-800"
                              : inv.zoho_push_status === "REJECTED_PLAYFIELD_DRAFT" ? "bg-red-100 text-red-800"
                              : inv.zoho_push_status === "PUSH_FAILED" ? "bg-red-100 text-red-800"
                              : inv.zoho_push_status ? "bg-amber-100 text-amber-800"
                              : "bg-zinc-100 text-zinc-600"
                            }`}>{inv.zoho_push_status || "—"}</span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setViewingInvoice(inv)}
                              className="h-7 text-xs"
                              data-testid={`inv-view-${inv.invoice_code}`}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            {isDraft && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openDraftForEdit(inv)}
                                className="h-7 text-xs ml-2"
                                data-testid={`inv-edit-draft-${inv.invoice_code}`}
                              >
                                Edit / Submit
                              </Button>
                            )}
                            {/* AC1/AC3 — visible ONLY when zoho_push_status is
                                exactly PUSH_PENDING. AC4 — button disables +
                                shows a spinner while its own retry is in flight. */}
                            {inv.zoho_push_status === "PUSH_PENDING" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRetryZohoPush(inv.invoice_code)}
                                disabled={retryingCode === inv.invoice_code}
                                className="h-7 text-xs ml-2"
                                data-testid={`inv-retry-zoho-${inv.invoice_code}`}
                              >
                                {retryingCode === inv.invoice_code ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Retrying…
                                  </>
                                ) : (
                                  "Retry Zoho Push"
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recon">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Open Deltas per (Vendor, Finished RM) — {recon.items?.length || 0} pair(s)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(recon.items?.length || 0) === 0 ? (
                <div className="p-8 text-center text-zinc-500" data-testid="recon-empty">No open deltas.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Vendor</th>
                      <th className="px-4 py-3 text-left">Finished RM</th>
                      <th className="px-4 py-3 text-right">Received</th>
                      <th className="px-4 py-3 text-right">Invoiced (approved)</th>
                      <th className="px-4 py-3 text-right">In-flight (draft)</th>
                      <th className="px-4 py-3 text-right">Open Δ</th>
                      <th className="px-4 py-3 text-right">Aging (days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recon.items.map((r, i) => {
                      const delta = Number(r.open_delta || 0);
                      const inFlight = Number(r.in_flight_qty || 0);
                      return (
                        <tr key={i} className="border-t hover:bg-zinc-50" data-testid={`recon-row-${r.vendor_id}-${r.finished_rm_id}`}>
                          <td className="px-4 py-3">{vendorName[r.vendor_id] || r.vendor_id}</td>
                          <td className="px-4 py-3 font-mono text-xs">{r.finished_rm_id}</td>
                          <td className="px-4 py-3 font-mono text-right">{Number(r.received_to_date).toLocaleString("en-IN")}</td>
                          <td className="px-4 py-3 font-mono text-right">{Number(r.invoiced_to_date).toLocaleString("en-IN")}</td>
                          <td className="px-4 py-3 font-mono text-right">
                            {inFlight > 0 ? (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[11px] font-semibold"
                                title={`${r.in_flight_invoice_count || 0} invoice(s) pushed to Zoho, awaiting approval`}
                                data-testid={`recon-in-flight-${r.vendor_id}-${r.finished_rm_id}`}
                              >
                                {Number(inFlight).toLocaleString("en-IN")}
                              </span>
                            ) : "—"}
                          </td>
                          <td className={`px-4 py-3 font-mono text-right font-bold ${delta > 0 ? "text-amber-700" : delta < 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {delta > 0 ? "+" : ""}{Number(delta).toLocaleString("en-IN")}
                          </td>
                          <td className="px-4 py-3 text-xs text-right">{r.aging_days ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Create dialog ─────────────────────────────────────────── */}
      <Dialog
        open={showCreate}
        onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}
      >
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="inv-create-dialog">
          <DialogHeader><DialogTitle>New Jobwork Invoice</DialogTitle></DialogHeader>

          {/* Vendor picker — outside the shared header so it drives the item-search endpoint. */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-xs font-bold uppercase">Vendor *</Label>
              <Select value={header.vendor_id || undefined} onValueChange={onVendorPick}>
                <SelectTrigger data-testid="new-inv-vendor"><SelectValue placeholder="Select vendor…" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {header.vendor_id && (
                <p className="text-[11px] text-zinc-500 mt-1" data-testid="inv-available-items-hint">
                  {loadingItems ? "Loading uninvoiced items…" : `${availableItems.length} item(s) with pending invoice qty for this vendor.`}
                </p>
              )}
            </div>
          </div>

          <BillHeaderFields
            data={header}
            onChange={setHeader}
            showOrderNumber={false}
            billNumberTestId="new-inv-bill-number"
            billDateTestId="new-inv-bill-date"
            billNumberPlaceholder="Vendor's invoice number"
          />

          <Separator className="my-4" />

          <BillLineItemsTable
            lineItems={lineItems}
            onLineItemsChange={setLineItems}
            items={availableItems}
            itemSearchLabel="Item Details"
            emptyItemsHint={header.vendor_id ? "No uninvoiced items for this vendor" : "Select a vendor to load items"}
            zohoTaxes={zohoTaxes}
            zohoAccounts={zohoAccounts}
            showAccountColumn={zohoConfigured}
            defaultAccountId={defaultJobworkAccountId}
            capQtyToAvailable={true}
          />

          <Separator className="my-4" />

          {/* Notes + Attachments + Totals grid — same layout as RM Inward. */}
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase">Notes / Remarks</Label>
                <Textarea
                  value={header.notes || ""}
                  onChange={(e) => setHeader({ ...header, notes: e.target.value })}
                  placeholder="Reconciliation reference, batch numbers, etc."
                  rows={4}
                  data-testid="new-inv-notes"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Vendor Invoice Files (optional)</Label>
                <div className="border-2 border-dashed rounded-lg p-3 mt-1">
                  <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-zinc-600 hover:text-zinc-900">
                    <Paperclip className="w-4 h-4" />
                    <span>Attach files (JPEG/PNG/WebP/HEIC/PDF, ≤5 MB each)</span>
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf"
                      onChange={(e) => { attachInvoiceFiles(e.target.files); e.target.value = ""; }}
                      className="hidden"
                      data-testid="new-inv-attach-input"
                    />
                  </label>
                  {invoiceFiles.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs">
                      {invoiceFiles.map((f, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="truncate flex-1">{f.name} <span className="text-zinc-500">({(f.size / 1024).toFixed(1)} KB)</span></span>
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => setInvoiceFiles((prev) => prev.filter((_, x) => x !== i))}
                            aria-label={`Remove ${f.name}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <BillTotalsPanel
              lineItems={lineItems}
              totals={totals}
              setTotals={setTotals}
              zohoTaxes={zohoTaxes}
              tdsTaxes={tdsTaxes}
            />
          </div>

          {shortfall && (
            <div className="border border-red-300 bg-red-50 rounded p-3 text-xs text-red-800 mt-4" data-testid="inv-shortfall">
              <div className="font-semibold mb-1">Over-billed — invoice blocked</div>
              <pre className="whitespace-pre-wrap font-mono text-[11px]">{shortfall}</pre>
            </div>
          )}

          {pushError && (
            <div className="border border-red-300 bg-red-50 rounded p-3 text-xs text-red-800 mt-4" data-testid="inv-push-error">
              <div className="font-semibold mb-1">
                Zoho rejected the push {pushError.code ? `(${pushError.code})` : ""} — saved as Playfield Draft
              </div>
              <div className="whitespace-pre-wrap">{pushError.message}</div>
              <div className="text-[11px] mt-1 text-red-600">
                Fix the field above and click <span className="font-semibold">Save to Zoho</span> again to retry.
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={savingDraft || creating}
              data-testid="btn-inv-save-draft"
            >
              {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Draft"}
            </Button>
            <PermButton entity="JobworkInvoice" action="CREATE" onClick={submitCreate} disabled={creating || savingDraft} data-testid="btn-inv-commit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCode ? "Save to Zoho" : "Save to Zoho")}
            </PermButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── View dialog (read-only) ─────────────────────────────── */}
      <Dialog open={!!viewingInvoice} onOpenChange={(o) => { if (!o) setViewingInvoice(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="inv-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Jobwork Invoice
              <span className="font-mono text-sm text-zinc-600">
                {viewingInvoice?.invoice_code}
              </span>
            </DialogTitle>
          </DialogHeader>
          {viewingInvoice && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Bill #</div>
                  <div className="font-mono text-xs" data-testid="view-bill-number">{viewingInvoice.bill_number || viewingInvoice.invoice_code}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Bill Date</div>
                  <div className="font-mono text-xs">{viewingInvoice.bill_date || viewingInvoice.invoice_date || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Due Date</div>
                  <div className="font-mono text-xs">{viewingInvoice.due_date || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Vendor</div>
                  <div className="text-xs">{vendorName[viewingInvoice.vendor_id] || viewingInvoice.vendor_id}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Status</div>
                  <div className="font-mono text-xs" data-testid="view-status">{viewingInvoice.status || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Zoho Push</div>
                  <div className="font-mono text-xs" data-testid="view-zoho-status">{viewingInvoice.zoho_push_status || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Zoho Bill ID</div>
                  <div className="font-mono text-xs">{viewingInvoice.zoho_bill_id || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-zinc-500">Payment Terms</div>
                  <div className="font-mono text-xs">{viewingInvoice.payment_terms || "—"}</div>
                </div>
              </div>

              {(viewingInvoice.zoho_error || viewingInvoice.zoho_push_error) && (
                <div className="border border-red-300 bg-red-50 rounded p-3 text-xs text-red-800" data-testid="view-zoho-error">
                  <div className="font-semibold mb-1">
                    Zoho Error {viewingInvoice.zoho_push_error_code ? `(${viewingInvoice.zoho_push_error_code})` : ""}
                  </div>
                  <div className="whitespace-pre-wrap">
                    {viewingInvoice.zoho_error || viewingInvoice.zoho_push_error}
                  </div>
                </div>
              )}

              {/* Feb 2026 v2 — invoice-level "Sync with Zoho" (finance).
                  Visible when either branch is applicable: push (PUSH_PENDING/
                  NEEDS_REZOHO) OR status-sync (PUSHED with a linked bill).
                  Matches the attachment sync button's pattern; backend
                  dispatches to the right sub-flow. */}
              {(
                ["PUSH_PENDING", "NEEDS_REZOHO"].includes(viewingInvoice.zoho_push_status)
                || (viewingInvoice.zoho_push_status === "PUSHED" && viewingInvoice.zoho_bill_id)
              ) && (
                <div className="flex items-center justify-between gap-2 border border-zinc-200 bg-zinc-50 rounded p-3">
                  <div className="text-xs">
                    <div className="font-semibold text-zinc-700">Sync with Zoho</div>
                    <div className="text-zinc-500">
                      {["PUSH_PENDING", "NEEDS_REZOHO"].includes(viewingInvoice.zoho_push_status)
                        ? "Retry the Zoho push. If a matching bill already exists it will be adopted; otherwise a new bill is created."
                        : "Pull the current Zoho bill status and apply the same approve / reject as the webhook."}
                    </div>
                  </div>
                  <PermButton
                    entity="JobWorkInvoice" action="UPDATE"
                    variant="outline" size="sm"
                    onClick={handleSyncJwInvoiceWithZoho}
                    disabled={zohoInvSyncing}
                    data-testid="jw-invoice-sync-with-zoho-btn"
                  >
                    {zohoInvSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Sync with Zoho
                  </PermButton>
                </div>
              )}

              <Separator />

              <div>
                <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Line Items ({(viewingInvoice.line_items || []).length})</div>
                <div className="overflow-x-auto rounded border">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50 text-zinc-500 uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">RM</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">HSN</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-left">Unit</th>
                        <th className="px-3 py-2 text-right">Rate</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewingInvoice.line_items || []).map((li, i) => (
                        <tr key={`${li.rm_id}-${i}`} className="border-t" data-testid={`view-line-${i}`}>
                          <td className="px-3 py-2 font-mono">{li.rm_id}</td>
                          <td className="px-3 py-2">{asText(li.description) || "—"}</td>
                          <td className="px-3 py-2 font-mono">{asText(li.hsn_or_sac) || "—"}</td>
                          <td className="px-3 py-2 font-mono text-right">{Number(li.quantity || 0).toLocaleString("en-IN")}</td>
                          <td className="px-3 py-2">{li.unit || "—"}</td>
                          <td className="px-3 py-2 font-mono text-right">{Number(li.rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 font-mono text-right">{Number(li.amount || (li.quantity || 0) * (li.rate || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8 pt-2">
                <div>
                  {viewingInvoice.notes && (
                    <>
                      <div className="text-[11px] uppercase text-zinc-500">Notes</div>
                      <div className="text-xs whitespace-pre-wrap">{viewingInvoice.notes}</div>
                    </>
                  )}
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-zinc-500">Sub Total</span><span className="font-mono">{Number(viewingInvoice.sub_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                  {Number(viewingInvoice.discount_amount || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-zinc-500">Discount</span><span className="font-mono">-{Number(viewingInvoice.discount_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-zinc-500">Tax Total</span><span className="font-mono">{Number(viewingInvoice.tax_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                  <Separator />
                  <div className="flex justify-between font-bold pt-1"><span>Grand Total</span><span className="font-mono" data-testid="view-grand-total">{Number(viewingInvoice.grand_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                </div>
              </div>

              {/* Attachments — Feb 2026 per-attachment Zoho sync status
                  (mirrors RM Inward's `RMInwardBillDetailDialog`). */}
              {Array.isArray(viewingInvoice.attachments) && viewingInvoice.attachments.filter((a) => a && !a.is_deleted).length > 0 && (() => {
                const active = viewingInvoice.attachments.filter((a) => a && !a.is_deleted);
                const anyUnsynced = active.some((a) => !a.zoho_synced);
                return (
                  <div className="p-3 border rounded" data-testid="jw-attachments-block">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                        <Paperclip className="w-3 h-3" /> Attachments ({active.length})
                      </div>
                      {viewingInvoice.zoho_bill_id && anyUnsynced && (
                        <PermButton
                          entity="JobworkInvoice" action="UPDATE"
                          variant="outline" size="sm"
                          onClick={handleSyncJwAttachmentsToZoho}
                          disabled={zohoAttSyncing}
                          data-testid="jw-attachment-sync-to-zoho-btn"
                        >
                          {zohoAttSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Sync to Zoho
                        </PermButton>
                      )}
                    </div>
                    <div className="space-y-1">
                      {active.map((a) => (
                        <div
                          key={a.id || a.storage_path}
                          className="flex items-center justify-between gap-2 bg-zinc-50 border rounded px-2 py-1.5 text-[11px]"
                          data-testid={`jw-attachment-row-${a.id || ""}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Paperclip className="w-3 h-3 text-zinc-400 shrink-0" />
                            <span className="truncate text-zinc-800 font-medium" title={a.original_filename}>
                              {a.original_filename || "(unnamed)"}
                            </span>
                            <span className="text-zinc-500 whitespace-nowrap font-mono text-[10px]">
                              {(Number(a.size || 0) / 1024).toFixed(0)} KB
                            </span>
                          </div>
                          {viewingInvoice.zoho_bill_id && (
                            a.zoho_synced ? (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200"
                                title={`Zoho doc: ${a.zoho_document_id || "?"}`}
                                data-testid={`jw-attachment-zoho-badge-synced-${a.id}`}
                              >
                                <CheckCircle2 className="w-2.5 h-2.5" /> Synced
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300"
                                title={a.zoho_last_error || "Not yet synced to Zoho"}
                                data-testid={`jw-attachment-zoho-badge-pending-${a.id}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" /> Not synced
                              </span>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingInvoice(null)} data-testid="view-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
